import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import cn from 'classnames';
import { usePermissionAny } from '../../hooks';
import {
  getCathedra,
  getCathedraDepartments,
  getCathedraSpecialties,
  getCathedraDisciplines,
  getCathedraAdditionalWork,
  getCathedraPersonnel,
  getCathedraFiles,
  getCathedraEquipment,
} from '../../actions/cathedras';
import Main from './tabs/Main';
import Departments from './tabs/Departments';
import Specialties from './tabs/Specialties';
import Disciplines from './tabs/Disciplines';
import AdditionalWork from './tabs/AdditionalWork';
import Personnel from './tabs/Personnel';
import Files from './tabs/Files';
import Equipment from './tabs/Equipment';
import styles from './card.module.css';

/* Карточка кафедры — окно поверх списка, только просмотр: кнопки «Сохранить» в
   нём нет, единственное действие — «Закрыть».

   Вкладки грузятся по требованию (п. 4.7): при открытии запрашивается только
   «Главная», остальные — при первом переходе. Загруженное лежит в state до
   закрытия окна и повторно не запрашивается; учебный год «Доп. учебной работы»
   кешируется отдельным ключом — у каждого года свои значения. */

const TABS = [
  { key: 'main', label: 'Главная' },
  { key: 'departments', label: 'Подчинённые подразделения' },
  { key: 'specialties', label: 'Воен. специальности и УП' },
  { key: 'disciplines', label: 'Дисциплины' },
  { key: 'work', label: 'Дополнительная учебная работа' },
  { key: 'personnel', label: 'Штат' },
  { key: 'files', label: 'Файлы' },
  { key: 'equipment', label: 'Материально-технические средства' },
];

// счётчик записей в пилюле есть только у вкладок с таблицами
const COUNTED = ['departments', 'specialties', 'disciplines', 'personnel', 'files', 'equipment'];

// вкладки, скрываемые при отказе сервера по правам (403) — запасная проверка
// на случай, когда доступ к объекту закрыт не по access_modes, а по кафедре
const HIDDEN_ON_403 = ['disciplines', 'equipment'];

/* Вкладки «Дисциплины» и «Материально-технические средства» закрыты отдельными
   правами на объекты раздела «Справочники». При отсутствии права вкладку не
   показываем вовсе — даже неактивной для выбора (US-64, US-65). Право
   проверяется по access_modes на клиенте; серверный 403 остаётся запасным
   рубежом (HIDDEN_ON_403 выше). */
const DISCIPLINES_ACCESS = [
  ['Справочники', 'Дисциплины. Дисциплины РПД.'],
  ['Справочники', 'Дисциплины. Дисциплины РПД. Администратор'],
];
const EQUIPMENT_ACCESS = [
  ['Справочники', 'Материально-технические средства'],
  ['Справочники', 'Материально-технические средства. Администратор'],
];

const requestOf = (tab, id, year) => {
  if (tab === 'departments') return getCathedraDepartments(id);
  if (tab === 'specialties') return getCathedraSpecialties(id);
  if (tab === 'disciplines') return getCathedraDisciplines(id);
  if (tab === 'work') return getCathedraAdditionalWork(id, year);
  if (tab === 'personnel') return getCathedraPersonnel(id);
  if (tab === 'files') return getCathedraFiles(id);
  if (tab === 'equipment') return getCathedraEquipment(id);
  return getCathedra(id);
};

const keyOf = (tab, year) => (tab === 'work' ? `work:${year || ''}` : tab);

const Card = ({ id, onClose }) => {
  const dispatch = useDispatch();

  const { readRight: canDisciplines } = usePermissionAny(DISCIPLINES_ACCESS);
  const { readRight: canEquipment } = usePermissionAny(EQUIPMENT_ACCESS);

  const [store, setStore] = useState({});
  const [tab, setTab] = useState('main');
  // null — учебный год не выбирали: сервер сам вернёт текущий вместе с перечнем
  const [year, setYear] = useState(null);

  const aliveRef = useRef(true);
  const idRef = useRef(id);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    idRef.current = id;
    setStore({});
    setTab('main');
    setYear(null);
  }, [id]);

  const key = keyOf(tab, year);
  const entry = store[key];

  useEffect(() => {
    if (entry) return;
    setStore(prev => ({ ...prev, [key]: { status: 'loading' } }));
    (async () => {
      const result = await dispatch(requestOf(tab, id, year));
      if (!aliveRef.current || idRef.current !== id) return;
      setStore(prev => ({
        ...prev,
        [key]: result.error
          ? { status: 'error', error: result.error, code: result.status }
          : { status: 'ok', data: result.data },
      }));
    })();
  }, [dispatch, entry, key, tab, id, year]);

  const deniedByServer = HIDDEN_ON_403.filter(
    item => store[item] && store[item].status === 'error' && store[item].code === 403
  );

  // нет права на объект — вкладки нет в ряду; плюс запасная серверная проверка
  const hiddenTabs = [
    ...deniedByServer,
    ...(canDisciplines ? [] : ['disciplines']),
    ...(canEquipment ? [] : ['equipment']),
  ];

  useEffect(() => {
    if (hiddenTabs.indexOf(tab) >= 0) setTab('main');
  }, [hiddenTabs, tab]);

  // пока окно открыто, страница под ним не прокручивается; ширина исчезнувшего
  // скроллбара компенсируется отступом, чтобы страница не дёрнулась вправо
  useEffect(() => {
    const { body } = document;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const counterOf = useCallback((tabKey) => {
    const loaded = store[tabKey];
    return loaded && loaded.status === 'ok' ? loaded.data.count : null;
  }, [store]);

  const mainEntry = store.main;
  const main = mainEntry && mainEntry.status === 'ok' ? mainEntry.data : null;

  // перечень учебных лет одинаков для всех годов — берётся из любого уже
  // полученного ответа вкладки, чтобы не пропадать на время загрузки
  const workYears = Object.keys(store)
    .filter(item => item.indexOf('work:') === 0)
    .map(item => store[item].data && store[item].data.years)
    .find(Boolean) || [];

  const renderBody = () => {
    if (tab === 'main') {
      if (!mainEntry || mainEntry.status === 'loading') {
        return (
          <div className={styles.loader}>
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        );
      }
      if (mainEntry.status === 'error') {
        return (
          <div className={styles.error}>
            <div className={styles.errorTitle}>{mainEntry.error}</div>
            {mainEntry.code === 403
              ? 'Записи этой кафедры вам недоступны'
              : 'Данные не получены'}
          </div>
        );
      }
      return <Main data={mainEntry.data} />;
    }

    if (tab === 'work') {
      const loading = !entry || entry.status === 'loading';
      if (loading && !workYears.length) {
        return (
          <div className={styles.loader}>
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        );
      }
      if (entry && entry.status === 'error') {
        return (
          <div className={styles.error}>
            <div className={styles.errorTitle}>{entry.error}</div>
            Данные не получены
          </div>
        );
      }
      const data = entry && entry.status === 'ok' ? entry.data : null;
      return (
        <AdditionalWork
          id={id}
          data={data}
          years={data ? data.years : workYears}
          year={year == null && data ? data.year : year}
          loading={loading}
          onYearChange={setYear}
        />
      );
    }

    if (!entry || entry.status === 'loading') {
      return (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      );
    }

    if (entry.status === 'error') {
      return (
        <div className={styles.error}>
          <div className={styles.errorTitle}>{entry.error}</div>
          {entry.code === 403
            ? 'Записи этой кафедры вам недоступны'
            : 'Данные не получены'}
        </div>
      );
    }

    if (tab === 'departments') return <Departments data={entry.data} />;
    if (tab === 'specialties') return <Specialties data={entry.data} />;
    if (tab === 'disciplines') return <Disciplines data={entry.data} />;
    if (tab === 'personnel') return <Personnel data={entry.data} />;
    if (tab === 'files') return <Files id={id} data={entry.data} />;
    if (tab === 'equipment') return <Equipment data={entry.data} />;
    return null;
  };

  const subtitle = main
    ? [
      main.shortname && `Сокращение: ${main.shortname}`,
      main.code && `Код подразделения: ${main.code}`,
      main.parent_name,
    ].filter(Boolean).join('  ·  ')
    : '';

  return createPortal(
    <div
      className={styles.sheetOv}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.sheet}>
        <div className={styles.sheetTitle}>
          <h2>{main ? main.name : 'Кафедра'}</h2>
          {!!subtitle && <div className={styles.sheetSub}>{subtitle}</div>}
        </div>

        <div className={styles.tabs}>
          {TABS
            .filter(item => hiddenTabs.indexOf(item.key) < 0)
            .map((item) => {
              const count = COUNTED.indexOf(item.key) >= 0 ? counterOf(item.key) : null;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={cn({ [styles.tabActive]: item.key === tab })}
                  onClick={() => setTab(item.key)}
                >
                  {item.label}
                  {count != null && <span className={styles.tabCount}>{count}</span>}
                </button>
              );
            })}
        </div>

        <div className={styles.sheetBody}>{renderBody()}</div>

        <div className={styles.sheetFoot}>
          <button type="button" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Card;
